// Video Section JavaScript
(() => {
  'use strict';

  const videoPlaybackRequests = new WeakMap();

  // Utility functions
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const getBreakpoint = () => {
    if (window.innerWidth >= 768) return 'desktop';
    return 'mobile';
  };

  const updateVideoSources = (video, sources) => {
    if (!video || !sources || !sources.length) return;

    // Remove existing sources
    const existingSources = video.querySelectorAll('source');
    existingSources.forEach(source => source.remove());

    // Add new sources
    sources.forEach(source => {
      const sourceElement = document.createElement('source');
      sourceElement.src = source.url;
      sourceElement.type = source.mime_type;
      video.appendChild(sourceElement);
    });
  };

  const getVideoConfig = (sectionId) => {
    const script = document.getElementById(`hero-video-${sectionId}--sources`);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Failed to parse video config:', error);
      return null;
    }
  };

  const playSectionVideo = (video) => {
    if (!video) return Promise.resolve(false);

    const pendingRequest = videoPlaybackRequests.get(video);
    if (pendingRequest) return pendingRequest;

    const playRequest = video.play();
    if (!playRequest || typeof playRequest.then !== 'function') {
      return Promise.resolve(true);
    }

    const handledRequest = playRequest
      .then(() => true)
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('Video playback failed:', error);
        }

        return false;
      })
      .finally(() => {
        if (videoPlaybackRequests.get(video) === handledRequest) {
          videoPlaybackRequests.delete(video);
        }
      });

    videoPlaybackRequests.set(video, handledRequest);
    return handledRequest;
  };

  // Video Section Class
  class VideoSection {
    constructor(element) {
      this.element = element;
      this.video = element.querySelector('[data-video-element]');
      this.playButton = element.querySelector('[data-play-button]');
      this.isPlaying = false;
      this.hasAutoPlayed = false;
      this.hasLoadedSources = false;
      this.currentBreakpoint = getBreakpoint();

      if (!this.video && !this.playButton) return;

      this.sectionId = this.#extractSectionId();
      this.config = getVideoConfig(this.sectionId);

      this.#init();
    }

    #extractSectionId() {
      const classes = this.element.className.split(' ');
      const sectionClass = classes.find(cls => cls.startsWith('video-section-'));
      return sectionClass ? sectionClass.replace('video-section-', '') : null;
    }

    #init() {
      this.#setupEventListeners();
      this.#setupResponsiveVideo();
      this.#setupLazyLoad();
      this.#setupAutoplay();
    }

    #setupEventListeners() {
      // Play button click
      if (this.playButton) {
        this.playButton.addEventListener('click', this.#handlePlayButtonClick.bind(this));
      }

      // Video click when playing (to pause)
      if (this.video) {
        this.video.addEventListener('click', this.#handleVideoClick.bind(this));
        this.video.addEventListener('ended', this.#handleVideoEnded.bind(this));
      }
    }

    #setupResponsiveVideo() {
      if (!this.video || !this.config) return;

      // Handle resize
      const handleResize = () => {
        const newBreakpoint = getBreakpoint();
        if (newBreakpoint === this.currentBreakpoint) return;

        this.currentBreakpoint = newBreakpoint;

        if (this.hasLoadedSources) {
          this.#updateVideoForBreakpoint(this.currentBreakpoint);
          this.video.load();
        }
      };

      window.addEventListener('resize', debounce(handleResize, 150));
    }

    #setupLazyLoad() {
      if (!this.video || !this.config) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.#ensureVideoLoaded();

            if (!this.config.autoplay) {
              observer.unobserve(this.element);
            }
          });
        },
        {
          threshold: 0.01,
          rootMargin: '300px 0px'
        }
      );

      observer.observe(this.element);
    }

    #ensureVideoLoaded() {
      if (!this.video || !this.config || this.hasLoadedSources) return;

      this.#updateVideoForBreakpoint(this.currentBreakpoint);
      this.hasLoadedSources = true;
    }

    #updateVideoForBreakpoint(breakpoint) {
      if (!this.config) return;

      let sources;
      if (breakpoint === 'desktop') {
        sources = this.config.video_desktop;
      } else {
        sources = this.config.video_mobile;
      }

      if (sources && sources.length > 0) {
        updateVideoSources(this.video, sources);
      }
    }

    #handlePlayButtonClick(event) {
      event.preventDefault();

      if (!this.video) return;

      this.#ensureVideoLoaded();
      this.video.load();
      this.#playVideo();
    }

    #handleVideoClick() {
      if (this.isPlaying) {
        this.#pauseVideo();
      }
    }

    #handleVideoEnded() {
      this.#resetVideo();
    }

    #playVideo() {
      if (!this.video) return;

      playSectionVideo(this.video).then((didStart) => {
        if (!didStart) return;

        this.isPlaying = true;
        this.video.controls = true;
        this.element.classList.add('video-section--playing');
      });
    }

    #pauseVideo() {
      if (!this.video) return;

      this.video.pause();
      this.isPlaying = false;
      this.element.classList.remove('video-section--playing');
    }

    #resetVideo() {
      if (!this.video) return;

      this.video.currentTime = 0;
      this.isPlaying = false;
      this.element.classList.remove('video-section--playing');
    }

    #setupAutoplay() {
      if (!this.config || !this.config.autoplay || !this.video) return;

      // Add autoplay class to element
      this.element.classList.add('video-section--autoplay');

      // Create intersection observer
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasAutoPlayed) {
              this.#handleAutoplay();
            }
          });
        },
        {
          threshold: 0.5, // Trigger when 50% of the video is visible
          rootMargin: '0px'
        }
      );

      observer.observe(this.element);
    }

    #handleAutoplay() {
      if (!this.video || this.hasAutoPlayed) return;

      this.hasAutoPlayed = true;
      this.#ensureVideoLoaded();

      // Set video attributes for autoplay
      this.video.muted = true; // Required for autoplay in most browsers
      this.video.loop = true;

      // Load now that we're about to play — deferred until this point
      // so the video doesn't buffer during initial page load and inflate LCP
      this.video.load();
      this.#playVideo();
    }
  }

  // Initialize video sections
  const initVideoSections = () => {
    const videoSections = document.querySelectorAll('.video-section');
    videoSections.forEach(section => new VideoSection(section));
  };

  // Initialize on DOM content loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoSections);
  } else {
    initVideoSections();
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.video-section');
    if (section) {
      new VideoSection(section);
    }
  });
})();
